/**
 * Ecom price calculator + form interactions.
 *
 * Owns the apps-config row builder, tier-based pricing math, stations stepper,
 * and the sign-up modal opener. Reads all data from `data-*` attributes on the
 * form, so the Twig templates stay declarative.
 *
 * Exports a single `initPriceCalculator(form)` function. Returns a controller
 * exposing `getApps()`, `getStations()`, `getSelectedPlanId()` so the SignUp
 * class can serialize state at submit time without re-reading the DOM.
 */

const parseNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const parsed = Number.parseFloat(String(value).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseTierRows = (raw) => {
  if (!raw) return [];
  try {
    const decoded = JSON.parse(raw);
    if (!Array.isArray(decoded)) return [];
    return decoded
      .map((row) => ({
        maxStations: Number.parseInt(
          row.col1 ?? row.max_stations ?? row.maxStations,
          10
        ),
        yearlyPrice: parseNumber(
          row.col2 ?? row.yearly_price ?? row.yearlyPrice
        ),
      }))
      .filter((row) => Number.isInteger(row.maxStations) && row.maxStations > 0)
      .sort((a, b) => a.maxStations - b.maxStations);
  } catch (e) {
    return [];
  }
};

const formatMoney = (amount) =>
  Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ordinalLabel = (index) => {
  const value = index + 1;
  if (value % 100 >= 11 && value % 100 <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
};

const STATION_WORD = (n) => (n === 1 ? 'station' : 'stations');

/**
 * Tiered yearly pricing for a single app row.
 * First station uses `priceFirst`; remaining broadcast stations fill tiers
 * in order; anything past the last tier is `exceedingPrice`. IP stations
 * (beyond the first, if first was IP) use `ipStationPrice`.
 */
const calculateYearlyForStations = (
  broadcastStations,
  ipStations,
  tiers,
  exceedingPrice,
  priceFirst,
  ipStationPrice
) => {
  const totalStations = broadcastStations + ipStations;
  if (totalStations === 0) return 0;

  let total = priceFirst;
  let remainingBroadcast = broadcastStations - 1;
  let prevEnd = 1;

  for (const tier of tiers) {
    if (remainingBroadcast <= 0) break;
    if (tier.maxStations <= prevEnd) continue;
    const capacity = tier.maxStations - prevEnd;
    const qty = Math.min(remainingBroadcast, capacity);
    total += qty * tier.yearlyPrice;
    remainingBroadcast -= qty;
    prevEnd = tier.maxStations;
  }
  if (remainingBroadcast > 0) total += remainingBroadcast * exceedingPrice;

  const remainingIp = ipStations - (broadcastStations === 0 ? 1 : 0);
  if (remainingIp > 0 && ipStationPrice > 0)
    total += remainingIp * ipStationPrice;

  return total;
};

const t = (el, key) => el.dataset[`i18n${key}`] || '';

export default class PriceCalculator {
  constructor(form) {
    this.form = form;
    this.isAppSection = form.dataset.isAppSection === '1';
    this.currencySymbol = form.dataset.currencySymbol || '€';

    // App rows (apps-config-list)
    this.appsList = document.getElementById('apps-config-list');
    this.addAppRowButton = document.getElementById('add-app-row');

    // Plan + stations (license summary)
    this.stationPlanSelect = document.getElementById('station-plan');
    this.stationsDec = document.querySelector('.js-stations-dec');
    this.stationsInc = document.querySelector('.js-stations-inc');
    this.stationsInput = document.querySelector('.js-stations');
    this.stationsDisplay = document.querySelector('.js-stations-display');

    // Summary blocks
    this.pricingSummary = document.getElementById('apps-pricing-summary');
    this.pricingSummarySetup = document.querySelector(
      '#apps-pricing-summary-setup [role="cost"]'
    );
    this.pricingSummaryAnnual = document.querySelector(
        '#apps-pricing-summary-annual [role="cost"]'
      );
    this.licenseCostOverview = document.getElementById('license-cost-overview');
    this.licenseCostSetup = document.getElementById('license-cost-setup');
    this.licenseCostMonthly = document.getElementById('license-cost-monthly');

    // Sign-up modal (license pages)
    this.modal = document.getElementById('sign-up-form-modal');
    this.modalCloseButton = document.getElementById(
      'sign-up-form-modal--close'
    );
    this.signUpButtons = document.querySelectorAll('[sign-up]');

    this.bindEvents();
    if (this.appsList) this.initAppRows();
    this.updateStationsDisplay();
    this.updateLicenseCostOverview();
    this.updatePricingSummary();
  }

  bindEvents() {
    if (this.addAppRowButton && this.appsList) {
      this.addAppRowButton.addEventListener('click', () => this.addAppRow());
    }
    if (this.stationPlanSelect) {
      this.stationPlanSelect.addEventListener('change', () => {
        this.updatePricingSummary();
        this.updateLicenseCostOverview();
      });
    }
    if (this.stationsDec) {
      this.stationsDec.addEventListener('click', () => this.bumpStations(-1));
    }
    if (this.stationsInc) {
      this.stationsInc.addEventListener('click', () => this.bumpStations(1));
    }

    if (this.modal) {
      this.signUpButtons.forEach((button) => {
        button.addEventListener('click', () => this.openModal(button));
      });
      this.modalCloseButton?.addEventListener('click', () => this.closeModal());
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal();
      });
    }
  }

  openModal(triggerButton) {
    this.modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    const licenseId = triggerButton.getAttribute('data-id');
    if (licenseId && this.stationPlanSelect) {
      this.stationPlanSelect.value = licenseId;
    }
    this.updatePricingSummary();
    this.updateLicenseCostOverview();
  }

  closeModal() {
    this.modal.classList.remove('open');
    document.body.style.overflow = '';
  }

  // ── Stations stepper ──────────────────────────────────────────────

  bumpStations(delta) {
    const current = Number.parseInt(this.stationsInput?.value || '1', 10) || 1;
    this.setStations(current + delta);
  }

  setStations(next) {
    const value = Math.max(1, Number.parseInt(String(next), 10) || 1);
    if (this.stationsInput) this.stationsInput.value = String(value);
    this.updateStationsDisplay();
    this.updateLicenseCostOverview();
    return value;
  }

  updateStationsDisplay() {
    if (!this.stationsDisplay) return;
    const value = Number.parseInt(this.stationsInput?.value || '1', 10) || 1;
    this.stationsDisplay.textContent = `${value}`;
  }

  getStations() {
    return Math.max(
      1,
      Number.parseInt(this.stationsInput?.value || '1', 10) || 1
    );
  }

  getSelectedPlanId() {
    return this.stationPlanSelect?.value || null;
  }

  // ── App rows ──────────────────────────────────────────────────────

  initAppRows() {
    this.appsList.appendChild(this.createAppRow(0));
    this.refreshRowLabels();
  }

  addAppRow() {
    const index = this.appsList.querySelectorAll('.js-app-config-row').length;
    this.appsList.appendChild(this.createAppRow(index));
    this.refreshRowLabels();
    this.updatePricingSummary();
  }

  refreshRowLabels() {
    const rows = this.appsList.querySelectorAll('.js-app-config-row');
    const labelTpl = t(this.form, 'AppOrderLabel') || 'Your %s app';
    const singleRow = rows.length <= 1;
    rows.forEach((row, index) => {
      const label = row.querySelector('.js-app-order-label');
      if (label)
        label.textContent = labelTpl.replace('%s', ordinalLabel(index));

      const removeButton = row.querySelector('.js-remove-app-row');
      if (removeButton) {
        removeButton.disabled = singleRow;
        removeButton.classList.toggle('opacity-50', singleRow);
        removeButton.classList.toggle('text-gray', singleRow);
        removeButton.classList.toggle('cursor-not-allowed', singleRow);
        removeButton.classList.toggle('hover:opacity-80', !singleRow);
        removeButton.classList.toggle('text-secondary', !singleRow);
      }
    });
  }

  createAppRow(index) {
    const tpl = t(this.form, 'AppRowTemplate') || this.defaultAppRowTemplate();
    const row = document.createElement('div');
    row.className = 'js-app-config-row';
    row.innerHTML = tpl
      .replace(/{index}/g, String(index + 1))
      .replace(/{ordinal}/g, ordinalLabel(index));

    const removeButton = row.querySelector('.js-remove-app-row');
    const appNameInput = row.querySelector('.js-app-name');
    const broadcastInput = row.querySelector('.js-broadcast-stations');
    const broadcastDisplay = row.querySelector('.js-broadcast-display');
    const broadcastDec = row.querySelector('.js-broadcast-dec');
    const broadcastInc = row.querySelector('.js-broadcast-inc');
    const ipInput = row.querySelector('.js-ip-stations');
    const ipDisplay = row.querySelector('.js-ip-display');
    const ipDec = row.querySelector('.js-ip-dec');
    const ipInc = row.querySelector('.js-ip-inc');

    appNameInput?.addEventListener('input', () => this.updatePricingSummary());
    broadcastDec?.addEventListener('click', () =>
      this.setRowCount(broadcastInput, broadcastDisplay, -1)
    );
    broadcastInc?.addEventListener('click', () =>
      this.setRowCount(broadcastInput, broadcastDisplay, 1)
    );
    ipDec?.addEventListener('click', () =>
      this.setRowCount(ipInput, ipDisplay, -1)
    );
    ipInc?.addEventListener('click', () =>
      this.setRowCount(ipInput, ipDisplay, 1)
    );

    removeButton?.addEventListener('click', () => {
      if (this.appsList.querySelectorAll('.js-app-config-row').length <= 1)
        return;
      row.remove();
      this.refreshRowLabels();
      this.updatePricingSummary();
    });

    this.setRowCount(broadcastInput, broadcastDisplay, 1);
    this.setRowCount(ipInput, ipDisplay, 0);

    return row;
  }

  setRowCount(input, display, nextValue) {
    const current = Number.parseInt(input?.value || '0', 10) || 0;
    const value = Math.max(0, current + nextValue);
    if (input) input.value = String(value);
    if (display) display.textContent = `${value}`;
    this.updatePricingSummary();
  }

  getApps() {
    const rows = Array.from(document.querySelectorAll('.js-app-config-row'));
    return rows.map((row, index) => {
      const appName =
        (row.querySelector('.js-app-name')?.value || '').trim() ||
        `App ${index + 1}`;
      const broadcastStations =
        Number.parseInt(
          row.querySelector('.js-broadcast-stations')?.value || '0',
          10
        ) || 0;
      const ipStations =
        Number.parseInt(
          row.querySelector('.js-ip-stations')?.value || '0',
          10
        ) || 0;
      return { name: appName, broadcastStations, ipStations };
    });
  }

  // ── Pricing summaries ─────────────────────────────────────────────

  updatePricingSummary() {
    if (
      !this.pricingSummary ||
      !this.pricingSummarySetup ||
      !this.pricingSummaryAnnual
    ) {
      return;
    }

    let setupFee = 0;
    let exceedingPrice = 0;
    let priceFirst = 0;
    let ipStationPrice = 0;
    let tiers = [];

    if (this.isAppSection) {
      setupFee = parseNumber(this.form.dataset.appSetupFee);
      exceedingPrice = parseNumber(this.form.dataset.appExceedingPrice);
      priceFirst = parseNumber(this.form.dataset.appPriceFirst);
      ipStationPrice = parseNumber(this.form.dataset.appIpStationPrice);
      tiers = parseTierRows(this.form.dataset.appPricingTiers || '[]');
    } else {
      if (!this.stationPlanSelect) {
        this.pricingSummary.classList.add('hidden');
        return;
      }
      const selectedOption =
        this.stationPlanSelect.options[this.stationPlanSelect.selectedIndex];
      if (!selectedOption) {
        this.pricingSummary.classList.add('hidden');
        return;
      }
      setupFee = parseNumber(selectedOption.dataset.setupFee);
      exceedingPrice = parseNumber(selectedOption.dataset.exceedingPrice);
      tiers = parseTierRows(selectedOption.dataset.tiers || '[]');
    }

    const apps = this.getApps();

    /*const apps = this.getApps().filter(
      (a) => a.broadcastStations + a.ipStations >= 1
    );
    if (!apps.length) {
      this.pricingSummary.classList.add('hidden');
      return;
    }*/

    let setupTotal = 0;
    let annualTotal = 0;
    apps.forEach((app) => {
      setupTotal += setupFee;
      annualTotal += calculateYearlyForStations(
        app.broadcastStations,
        app.ipStations,
        tiers,
        exceedingPrice,
        priceFirst,
        ipStationPrice
      );
    });

    const freeLabel = t(this.form, 'Free') || 'Free';
    const setupLabel = t(this.form, 'SetupFee') || 'Setup fee';
    const annualLabel = t(this.form, 'AnnualFee') || 'Annual fee';

    /*if (setupTotal <= 0 && annualTotal <= 0) {
      this.pricingSummarySetup.textContent = freeLabel;
      this.pricingSummaryAnnual.textContent = '';
      this.pricingSummary.classList.remove('hidden');
      return;
    }*/

    this.pricingSummarySetup.textContent =
      setupTotal > 0
        ? `${this.currencySymbol}${formatMoney(setupTotal)}`
        : `${this.currencySymbol}0`;
    this.pricingSummaryAnnual.textContent =
      annualTotal > 0
        ? `${this.currencySymbol}${formatMoney(annualTotal)}`
        : `${this.currencySymbol}0`;
    this.pricingSummary.classList.remove('hidden');
  }

  updateLicenseCostOverview() {
    if (
      !this.licenseCostOverview ||
      !this.licenseCostSetup ||
      !this.licenseCostMonthly ||
      !this.stationPlanSelect
    ) {
      return;
    }
    const selectedOption =
      this.stationPlanSelect.options[this.stationPlanSelect.selectedIndex];
    if (!selectedOption) {
      this.licenseCostOverview.classList.add('hidden');
      return;
    }

    const setupFee = parseNumber(selectedOption.dataset.setupFee);
    const fee = parseNumber(selectedOption.dataset.fee);
    const stations = this.getStations();
    const monthlyTotal = fee * stations;
    const freeLabel = t(this.form, 'Free') || 'Free';

    this.licenseCostSetup.textContent =
      setupFee > 0
        ? `${this.currencySymbol}${formatMoney(setupFee)}`
        : freeLabel;
    this.licenseCostMonthly.textContent = `${this.currencySymbol}${formatMoney(monthlyTotal)}`;
    this.licenseCostOverview.classList.remove('hidden');
  }

  /**
   * ponytail: App row HTML is templated server-side to keep Tailwind classes
   * co-located with the markup that uses them. Override this only if you
   * genuinely need dynamic row creation beyond the page-level template.
   */
  defaultAppRowTemplate() {
    return `
            <div class="text-display4 font-bold mb-4 text-primary js-app-order-label">Your {ordinal} app</div>
            <div class="grid grid-cols-1 laptop:grid-cols-[1fr_2fr] gap-4 items-end">
                <input
                    type="text"
                    class="js-app-name rounded-[25px] py-4 px-9 border-2 border-transparent bg-bgcolor-variation text-primary placeholder-primary/40 text-body"
                    placeholder="App name"
                    value=""
                    required
                />
                <div class="grid grid-cols-[1fr_1fr_60px] gap-4 items-end">
                    <div>
                        <div class="text-xs text-primary/70 mb-2">Broadcast stations</div>
                        <div class="w-full rounded-[25px] py-4 px-4 bg-bgcolor-variation text-primary flex items-center justify-between border-2 border-primary/20">
                            <button type="button" class="js-broadcast-dec text-primary text-3xl leading-none font-bold hover:opacity-70">−</button>
                            <div class="font-black text-small js-broadcast-display">0</div>
                            <button type="button" class="js-broadcast-inc text-primary text-3xl leading-none font-bold hover:opacity-70">+</button>
                            <input type="hidden" class="js-broadcast-stations" value="0" />
                        </div>
                    </div>
                    <div>
                        <div class="text-xs text-primary/70 mb-2">IP stations</div>
                        <div class="w-full rounded-[25px] py-4 px-4 bg-bgcolor-variation text-primary flex items-center justify-between border-2 border-primary/20">
                            <button type="button" class="js-ip-dec text-primary text-3xl leading-none font-bold hover:opacity-70">−</button>
                            <div class="font-black text-small js-ip-display">0</div>
                            <button type="button" class="js-ip-inc text-primary text-3xl leading-none font-bold hover:opacity-70">+</button>
                            <input type="hidden" class="js-ip-stations" value="0" />
                        </div>
                    </div>
                    <button
                        type="button"
                        class="js-remove-app-row rounded-full w-12 h-12 mb-2 bg-negative text-white text-3xl leading-none flex items-center justify-center hover:opacity-80 transition"
                        aria-label="Remove app"
                    >×</button>
                </div>
            </div>
        `;
  }
}
